// resolveParseOptions — single entry point for producing a `ParseOptions`
// snapshot valid at any offset in a document. Composes four tiers in priority
// order: in-buffer directives > connection > server-version probe > fallback.
// PRD § 2.5.

import { scanInBufferDirectives } from './inBufferDirectives';
import type {
  DirectiveOccurrence,
  ParseOptions,
  ResolveParseOptionsInput,
  ResolvedParseOptions,
  SqlEngine,
} from './types';

// Conservative fallbacks — over-accept rather than under-accept (G10).
const FALLBACK: Readonly<Record<SqlEngine, Readonly<ParseOptions>>> = {
  mssql: {
    engine: 'mssql',
    quotedIdentifier: true,
    ansiNulls: true,
    identifierFolding: 'case-insensitive',
  },
  postgres: {
    engine: 'postgres',
    standardConformingStrings: true,
    identifierFolding: 'lower',
  },
  mysql: {
    engine: 'mysql',
    ansiQuotes: false,
    isMariaDb: false,
    mysqlVersionGate: 'ge80',
    currentDelimiter: ';',
    identifierFolding: 'case-insensitive',
  },
  oracle: {
    engine: 'oracle',
    identifierFolding: 'upper',
  },
  sqlite: {
    engine: 'sqlite',
    identifierFolding: 'case-insensitive',
  },
};

// Apply `version` to `base` by deriving engine-specific fields. Tier 3.
function applyVersionProbe(engine: SqlEngine, version: string | undefined): Partial<ParseOptions> {
  if (!version) return {};
  const out: Partial<ParseOptions> = { serverVersion: version };
  switch (engine) {
    case 'mysql': {
      // Major version 8+ → full CTE/window support; earlier versions gate.
      const m = version.match(/(\d+)\.(\d+)/);
      if (m) {
        const major = Number(m[1]);
        out.mysqlVersionGate = major >= 8 ? 'ge80' : 'lt80';
      }
      break;
    }
    case 'postgres': {
      // PG 9.1+ defaults standard_conforming_strings=on; set explicitly if
      // we can infer.
      const m = version.match(/(\d+)\.(\d+)/);
      if (m) {
        const major = Number(m[1]);
        const minor = Number(m[2]);
        out.standardConformingStrings = major > 9 || (major === 9 && minor >= 1);
      } else {
        out.standardConformingStrings = true;
      }
      break;
    }
    case 'oracle': {
      // Compat level gates JSON dot-notation (12c+) and more; stash raw.
      const m = version.match(/^(\d+)/);
      if (m) out.oracleCompatLevel = m[1];
      break;
    }
    // mssql, sqlite: the raw `serverVersion` is sufficient; no derived fields.
  }
  return out;
}

function mergeOptions(
  base: ParseOptions,
  ...layers: ReadonlyArray<Partial<ParseOptions> | undefined>
): ParseOptions {
  const out: ParseOptions = { ...base };
  for (const layer of layers) {
    if (!layer) continue;
    for (const k of Object.keys(layer) as Array<keyof ParseOptions>) {
      const v = layer[k];
      if (v === undefined) continue;
      // Type-narrowed assignment to the union field.
      (out as unknown as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

function applyDirectivesUpTo(
  base: ParseOptions,
  directives: readonly DirectiveOccurrence[],
  offset: number,
): ParseOptions {
  const applicable: Partial<ParseOptions>[] = [];
  for (const d of directives) {
    if (d.applyAt <= offset) applicable.push(d.effect);
    else break; // directives are in source order
  }
  return mergeOptions(base, ...applicable);
}

export function resolveParseOptions(input: ResolveParseOptionsInput): ResolvedParseOptions {
  const { engine, documentText, fromConnection, serverVersion, unbound } = input;
  const fallback = FALLBACK[engine];
  const probe = applyVersionProbe(engine, serverVersion);

  // Tier order (lowest → highest priority): fallback → probe → connection.
  // In-buffer directives are applied per-offset in `optionsAt`.
  const base: ParseOptions = mergeOptions(
    fallback,
    probe,
    unbound ? undefined : fromConnection,
  );

  const directives = scanInBufferDirectives(engine, documentText);

  return {
    base,
    directives,
    optionsAt(offset: number): ParseOptions {
      return applyDirectivesUpTo(base, directives, offset);
    },
  };
}
